import { Dialog } from 'primereact/dialog'
import React, { useEffect, useState } from 'react'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import SharedInputText from '../components/Shared/SharedInputText'
import SharedButton from '../components/Shared/SharedButton'
import {
  CreateBusiness,
  editBuisness,
  GetBusinessesList
} from '../store/slices/BusinessSlice'
import validSupergitHIS from '../api/validSupergitHIS'
import SharedDropdown from '../components/Shared/SharedDropdown'

import { useDispatch } from 'react-redux'
import useFetchData from '../hooks/useFetchData'

export default function CompanyModal({
  setCreateDialogVisible,
  createDialogVisible,
  onRefresh,
  editData = null
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isLoading: isActionLoading } = useSelector(state => state.business)
  const { data: tpaData, executeFetch: fetchTpas } = useFetchData()
  const [selectedTpa, setSelectedTpa] = useState(null)

  const tpaList = tpaData?.data || []

  useEffect(() => {
    if (createDialogVisible) {
      fetchTpas('POST', 'getTpa')
    }
  }, [createDialogVisible, fetchTpas])

  const initialValues = {
    // ... same as before
    company_license: editData?.company_license || '',
    short_name: editData?.short_name || '',
    name_en: editData?.name_en || '',
    name_ar: editData?.name_ar || '',
    tpa_license_id: editData?.tpa_license_id || '',
    tpa_name_en: editData?.tpa_name_en || '',
    tpa_name_ar: editData?.tpa_name_ar || '',
    address: editData?.address || '',
    contact_info: editData?.contact_info || '',
    vat_no: editData?.vat_no || '',
    cr_no: editData?.cr_no || '',
    street_en: editData?.street_en || '',
    street_ar: editData?.street_ar || '',
    building_number: editData?.building_number || '',
    additional_number: editData?.additional_number || '',
    city_en: editData?.city_en || '',
    city_ar: editData?.city_ar || '',
    postal_code: editData?.postal_code || '',
    state_en: editData?.state_en || '',
    state_ar: editData?.state_ar || '',
    district_en: editData?.district_en || '',
    district_ar: editData?.district_ar || '',
    country_code: editData?.country_code || 'SA',
    provider_code: editData?.provider_code || '',
    region: editData?.region || 'Jeddah'
  }

  const validationSchema = Yup.object().shape({
    company_license: Yup.string().required(t('required')),
    name_en: Yup.string().required(t('required')),
    name_ar: Yup.string().required(t('required')),
    vat_no: Yup.string().required(t('required')),
    street_en: Yup.string().required(t('required')),
    street_ar: Yup.string().required(t('required')),
    building_number: Yup.string().required(t('required')),
    additional_number: Yup.string().required(t('required')),
    city_en: Yup.string().required(t('required')),
    city_ar: Yup.string().required(t('required')),
    postal_code: Yup.string().required(t('required')),
    state_en: Yup.string().required(t('required')),
    state_ar: Yup.string().required(t('required')),
    district_en: Yup.string().required(t('required')),
    district_ar: Yup.string().required(t('required')),
    country_code: Yup.string().required(t('required')),
    provider_code: Yup.string().required(t('required')),
    region: Yup.string().required(t('required'))
  })

  return (
    <>
      <Dialog
        header={editData ? t('edit') : t('CreateCompany')}
        visible={createDialogVisible}
        onHide={() => {
          if (!createDialogVisible) return
          setCreateDialogVisible(false)
        }}
        pt={{
          root: { className: 'w-full lg:w-10/12 mx-4 overflow-auto' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-3' },
          closeButton: {
            className: 'hover:!bg-black/20 !outline-none !shadow-none'
          },
          closeButtonIcon: { className: '!text-white' }
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            if (editData) {
              const resultAction = await dispatch(
                editBuisness({
                  data: { ...values, tpa_id: values?.tpa },
                  id: editData?.id
                })
              )
              if (editBuisness.fulfilled.match(resultAction)) {
                if (onRefresh) onRefresh()
                resetForm()
                setCreateDialogVisible(false)
              }
            } else {
              const resultAction = await dispatch(
                CreateBusiness({
                  ...values,
                  tpa_id: values?.tpa,
                  license_id: values?.company_license
                })
              )
              if (CreateBusiness.fulfilled.match(resultAction)) {
                if (onRefresh) onRefresh()
                resetForm()
                setCreateDialogVisible(false)
              }
            }
          }}
        >
          {formik => (
            <form onSubmit={formik.handleSubmit}>
              <div className=''>
                <div className='bg-white rounded-lg  p-3'>
                  <h1 className='text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200'>
                    Basic Information
                  </h1>
                  <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full'>
                    <Field
                      as={SharedInputText}
                      label={t('name_en')}
                      name='name_en'
                      placeholder={`${t('enter')} ${t('name_en')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('name_ar')}
                      name='name_ar'
                      placeholder={`${t('enter')} ${t('name_ar')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('short_name')}
                      name='short_name'
                      placeholder={`${t('enter')} ${t('short_name')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('company_license')}
                      name='company_license'
                      placeholder={`${t('enter')} ${t('company_license')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('address')}
                      name='address'
                      placeholder={`${t('enter')} ${t('address')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('contact_info')}
                      name='contact_info'
                      placeholder={t('contact_info_placeholder')}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('vat_no')}
                      name='vat_no'
                      placeholder={t('vat_no_placeholder')}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('cr_no')}
                      name='cr_no'
                      placeholder={t('cr_no_placeholder')}
                      className='!p-1.5'
                    />
                    <SharedDropdown
                      name='tpa'
                      label={t('tpa')}
                      options={tpaList?.map(item => ({
                        label: item?.name_en,
                        value: item?.id
                      }))}
                      onBlur={formik.handleBlur}
                      onChange={e => {
                        formik.handleChange(e)
                        const tpa = tpaList.find(tp => tp.id == e.value)
                        setSelectedTpa(tpa)
                        formik.setFieldValue('tpa_license_id', tpa?.license_id)
                        formik.setFieldValue('tpa_name_en', tpa?.name_en)
                        formik.setFieldValue('tpa_name_ar', tpa?.name_ar)
                      }}
                    />
                    {selectedTpa?.license_id == '' && (
                      <Field
                        as={SharedInputText}
                        label={t('tpa_license_id')}
                        name='tpa_license_id'
                        placeholder={`${t('enter')} ${t('tpa_license_id')}`}
                        className='!p-1.5'
                      />
                    )}
                    {/* {selectedTpa?.name_en == '' && (
                      <Field
                        as={SharedInputText}
                        label={`${t('TPA')} ${t('name_en')}`}
                        name='tpa_name_en'
                        placeholder={`${t('enter')} ${t('TPA')} ${t(
                          'name_en'
                        )}`}
                        className='!p-1.5'
                      />
                    )}
                    {selectedTpa?.name_ar == '' && (
                      <Field
                        as={SharedInputText}
                        label={`${t('TPA')} ${t('name_ar')}`}
                        name='tpa_name_ar'
                        placeholder={`${t('enter')} ${t('TPA')} ${t(
                          'name_ar'
                        )}`}
                        className='!p-1.5'
                      />
                    )} */}
                  </div>
                </div>
                <div className='bg-white rounded-lg  p-3'>
                  <h1 className='text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200'>
                    Address Information
                  </h1>
                  <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full'>
                    <Field
                      as={SharedInputText}
                      label={t('street_en')}
                      name='street_en'
                      placeholder={`${t('enter')} ${t('street_en')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('street_ar')}
                      name='street_ar'
                      placeholder={`${t('enter')} ${t('street_ar')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('building_number')}
                      name='building_number'
                      placeholder={`${t('enter')} ${t('building_number')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('additional_number')}
                      name='additional_number'
                      placeholder={`${t('enter')} ${t('additional_number')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('city_en')}
                      name='city_en'
                      placeholder={`${t('enter')} ${t('city_en')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('city_ar')}
                      name='city_ar'
                      placeholder={`${t('enter')} ${t('city_ar')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('postal_code')}
                      name='postal_code'
                      placeholder={`${t('enter')} ${t('postal_code')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('state_en')}
                      name='state_en'
                      placeholder={`${t('enter')} ${t('state_en')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('state_ar')}
                      name='state_ar'
                      placeholder={`${t('enter')} ${t('state_ar')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('district_en')}
                      name='district_en'
                      placeholder={`${t('enter')} ${t('district_en')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('district_ar')}
                      name='district_ar'
                      placeholder={`${t('enter')} ${t('district_ar')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('country_code')}
                      name='country_code'
                      placeholder={`${t('enter')} ${t('country_code')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('provider_code')}
                      name='provider_code'
                      placeholder={`${t('enter')} ${t('provider_code')}`}
                      className='!p-1.5'
                    />
                    <Field
                      as={SharedInputText}
                      label={t('region')}
                      name='region'
                      placeholder={`${t('enter')} ${t('region')}`}
                      className='!p-1.5'
                    />
                  </div>
                </div>
              </div>

              <div className='flex justify-end gap-6 mt-6'>
                <SharedButton
                  icon='pi pi-times'
                  label={t('Cancel')}
                  className='cancel-btn'
                  onClick={() => {
                    setCreateDialogVisible(false)
                    formik.resetForm()
                  }}
                  type='button'
                />
                <SharedButton
                  icon='pi pi-check'
                  disabled={isActionLoading}
                  loading={isActionLoading}
                  label={editData ? t('Update') : t('Submit')}
                  className='submit-btn'
                  type='submit'
                />
              </div>
            </form>
          )}
        </Formik>
      </Dialog>
    </>
  )
}
